import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Store, QrCode, Check, X, Loader2, LogOut, History, User,
  Mail, Calendar, Gift, AlertCircle, CheckCircle2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const PartnerPortalPage = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  
  // Code validation
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // History
  const [showHistory, setShowHistory] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/partners/me');
      setPartner(response.data);
    } catch (error) {
      setShowLogin(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);

    try {
      const response = await api.post('/partners/login', loginData);
      if (response.data.success) {
        setPartner(response.data.partner);
        setShowLogin(false);
        toast.success('¡Bienvenido!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/partners/logout');
    } catch (e) {
      // Ignore logout errors
    }
    setPartner(null);
    setShowLogin(true);
    toast.success('Sesión cerrada');
  };

  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    setValidating(true);
    setValidationResult(null);

    try {
      const response = await api.post('/partners/validate-code', { code: code.trim() });
      setValidationResult(response.data);
      if (response.data.success) {
        toast.success('¡Código válido!');
        setCode('');
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.already_redeemed) {
        setValidationResult({
          success: false,
          already_redeemed: true,
          redeemed_at: errorData.redeemed_at,
          redeemed_by_partner: errorData.redeemed_by_partner,
          message: errorData.message || 'Este código ya fue canjeado'
        });
      } else {
        setValidationResult({
          success: false,
          message: errorData?.detail || 'Código no encontrado o inválido'
        });
      }
      toast.error(errorData?.detail || 'Código inválido');
    } finally {
      setValidating(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/partners/redemptions');
      setRedemptions(response.data.redemptions || []);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  // Login Screen
  if (showLogin) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/30 rounded-2xl flex items-center justify-center mb-4">
              <Store size={40} className="text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-black uppercase tracking-tight">Portal Partners</h1>
            <p className="text-[#A1A1AA] text-sm mt-1">BRAVO PRODUCCIONES</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#52525B]">Email</Label>
              <Input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="tu@email.com"
                required
                className="mt-1 bg-[#0D0D0D] border-[#27272A] focus:border-[#D4AF37]"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-[#52525B]">Contraseña</Label>
              <Input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                required
                className="mt-1 bg-[#0D0D0D] border-[#27272A] focus:border-[#D4AF37]"
              />
            </div>

            <Button
              type="submit"
              disabled={loggingIn}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase tracking-wider py-6"
            >
              {loggingIn ? <Loader2 className="animate-spin" /> : 'Iniciar Sesión'}
            </Button>
          </form>

          <p className="text-center text-[#52525B] text-xs mt-6">
            ¿No tenés cuenta? Contactá al administrador
          </p>
        </motion.div>
      </div>
    );
  }

  // Main Partner Portal
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/30 rounded-lg flex items-center justify-center">
              <Store size={20} className="text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="font-bold text-white text-sm">{partner?.business_name}</h1>
              <p className="text-[#52525B] text-xs">Portal Partners</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={openHistory}
              className="text-[#A1A1AA] hover:text-white"
            >
              <History size={18} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#A1A1AA] hover:text-red-400"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Code Validation Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/30 rounded-2xl flex items-center justify-center mb-4">
              <QrCode size={32} className="text-[#D4AF37]" />
            </div>
            <h2 className="text-xl font-black uppercase tracking-tight">Validar Código BRAVX</h2>
            <p className="text-[#A1A1AA] text-sm mt-1">Ingresá el código del cliente para validar el beneficio</p>
          </div>

          <form onSubmit={handleValidateCode} className="max-w-md mx-auto">
            <div className="relative">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="BR-XXXXXXXX"
                className="text-center text-2xl font-mono tracking-widest py-6 bg-[#0D0D0D] border-[#27272A] focus:border-[#D4AF37] uppercase"
                maxLength={12}
              />
            </div>
            
            <Button
              type="submit"
              disabled={validating || !code.trim()}
              className="w-full mt-4 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase tracking-wider py-6"
            >
              {validating ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <Check className="mr-2" size={20} />
                  Validar Código
                </>
              )}
            </Button>
          </form>

          {/* Validation Result */}
          <AnimatePresence mode="wait">
            {validationResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`mt-6 p-6 rounded-xl border ${
                  validationResult.success
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    validationResult.success ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {validationResult.success ? (
                      <CheckCircle2 size={24} className="text-green-500" />
                    ) : (
                      <AlertCircle size={24} className="text-red-500" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className={`font-bold text-lg ${
                      validationResult.success ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {validationResult.success ? '¡Código Válido!' : validationResult.message}
                    </h3>

                    {validationResult.success && validationResult.benefit && (
                      <div className="mt-4 space-y-3">
                        {/* Benefit Info */}
                        <div className="bg-[#0D0D0D] rounded-lg p-4">
                          <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Beneficio</p>
                          <p className="text-white font-bold text-lg">{validationResult.benefit.title}</p>
                          {validationResult.benefit.description && (
                            <p className="text-[#A1A1AA] text-sm mt-1">{validationResult.benefit.description}</p>
                          )}
                        </div>

                        {/* User Info */}
                        {validationResult.user && (
                          <div className="bg-[#0D0D0D] rounded-lg p-4">
                            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Cliente</p>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#27272A] rounded-full flex items-center justify-center">
                                <User size={20} className="text-[#D4AF37]" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{validationResult.user.name}</p>
                                {validationResult.user.dni && (
                                  <p className="text-[#A1A1AA] text-sm font-mono">DNI: {validationResult.user.dni}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
                          <Sparkles size={16} />
                          <span>Canjeado exitosamente</span>
                        </div>
                      </div>
                    )}

                    {validationResult.already_redeemed && (
                      <div className="mt-3 text-sm text-[#A1A1AA]">
                        <p>Canjeado el: {new Date(validationResult.redeemed_at).toLocaleString('es-AR')}</p>
                        {validationResult.redeemed_by_partner && (
                          <p>Por: {validationResult.redeemed_by_partner}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* History Modal */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#121212] border border-[#27272A] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <History size={20} className="text-[#D4AF37]" />
                  Historial de Canjes
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                  <X size={20} />
                </Button>
              </div>

              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {loadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
                  </div>
                ) : redemptions.length === 0 ? (
                  <div className="text-center py-8">
                    <Gift size={48} className="mx-auto text-[#27272A] mb-4" />
                    <p className="text-[#A1A1AA]">No hay canjes registrados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {redemptions.map((r, index) => (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-[#0D0D0D] border border-[#27272A] rounded-lg p-3"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white">{r.benefit_title}</p>
                            <p className="text-[#A1A1AA] text-sm">{r.user_name}</p>
                            <p className="text-[#52525B] text-xs font-mono mt-1">{r.code}</p>
                          </div>
                          <span className="text-[#52525B] text-xs">
                            {new Date(r.created_at).toLocaleDateString('es-AR')}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerPortalPage;
